import { useMutation } from "@apollo/client";
import { v4 as uuidv4 } from "uuid";
import { gql } from "__generated__/gql";
import useCreateValidSubmitHandler from "components/Shared/Forms/useCreateValidSubmitHandler";
import {
  processApolloError,
  OrderingPlatformFormFields,
} from "components/Shared/OrderingPlatformDirectory/Form";
import { addNewRecordsWithFieldSpec } from "utils/apollo/cacheHelpers";
import { ADMIN_CONTEXT } from "utils/apollo/hooks/admin";
import { getActiveBrandIdContext } from "utils/apollo/hooks/brand";
import { assertBrandId, assertPresence } from "utils/assertions";
import { useCurrentBrandId } from "utils/currentUserHooks";
import { newDistributionCenterCacheUpdate } from "./DistributionCenters/Selected/utils";

export const createMutation = gql(/* GraphQL */ `
  mutation CreateOrderingPlatform(
    $orderingPlatformData: OrderingPlatformInput!
  ) {
    createOrderingPlatform(input: { orderingPlatform: $orderingPlatformData }) {
      orderingPlatform {
        id
        name
        ...OrderingPlatformDetails
        ...EditOrderingPlatformDetails
        ...OrderingPlatformDeleteMenuItemFragment
        ...OrderingPlatformsDirectoryTableRow
        distributionCentersList {
          id
          name
          fullDisplayName
          orderingPlatform {
            id
          }
          ...CreateDistributionCenterCacheUpdate
        }
      }
    }
  }
`);

export function useCreateOrderingPlatform(isAdmin = false) {
  const brandId = useCurrentBrandId();

  const [performCreate, { loading: isSaving }] = useMutation(createMutation, {
    context: isAdmin
      ? ADMIN_CONTEXT
      : brandId && getActiveBrandIdContext(brandId),
    update: (cache, result) => {
      const newOrderingPlatform =
        result.data?.createOrderingPlatform?.orderingPlatform;

      assertPresence(newOrderingPlatform);

      addNewRecordsWithFieldSpec({
        cache,
        baseFieldName: "orderingPlatforms",
        newObjs: [newOrderingPlatform],
      });

      const newDistributionCenters =
        result.data?.createOrderingPlatform?.orderingPlatform
          ?.distributionCentersList;
      assertPresence(newDistributionCenters);
      newDistributionCenterCacheUpdate(cache, newDistributionCenters);

      if (!isAdmin) {
        assertBrandId(brandId);
        addNewRecordsWithFieldSpec({
          cache,
          baseFieldName: "orderingPlatformsForBrand",
          newObjs: [newOrderingPlatform],
          getArgs: (existing) => ({ ...existing, brandId }),
        });
      }
    },
  });

  const [handleValidSubmit] = useCreateValidSubmitHandler(
    (formData: OrderingPlatformFormFields) => {
      if (!isAdmin) assertBrandId(brandId);

      const id = uuidv4();

      return performCreate({
        variables: {
          orderingPlatformData: {
            ...formData,
            id,
            forBrandId: isAdmin ? null : brandId,
          },
        },
      });
    },
    {
      processApolloError,
      successMessage: "Customer created successfully.",
    },
  );

  return { brandId, handleValidSubmit, isSaving };
}
